import React, { useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStore } from '@/store';
import { Button } from '@/components/ui/button';
import { 
  ChevronDown, 
  Loader2,
  Newspaper,
  Eye,
  BarChart2,
  Settings,
  Bell,
  Lightbulb,
  Cog
} from "lucide-react";
import { Stream, Workspace } from '@/store';
import { cn } from '@/utils/utils';
import { Tooltip, TooltipProvider } from '@/components/ui/tooltip';

interface UnifiedSelectionHeaderProps {
  type: 'stream' | 'studio';
  onStreamChange?: (streamId: number | null) => void;
  onWorkspaceChange?: (workspaceId: number | null) => void;
}

// Type guard functions
function isStream(item: Stream | Workspace): item is Stream {
  return 'stream_id' in item;
}

function isWorkspace(item: Stream | Workspace): item is Workspace {
  return 'workspace_id' in item;
}

// Helper functions to get properties based on type
function getId(item: Stream | Workspace): number {
  return isStream(item) ? item.stream_id : item.workspace_id;
}

function getName(item: Stream | Workspace): string {
  return isStream(item) ? item.stream_topic : item.workspace_name;
}

function getDescription(item: Stream | Workspace): string {
  return isStream(item) ? item.stream_description : item.workspace_description;
}

export function UnifiedSelectionHeader({ 
  type, 
  onStreamChange, 
  onWorkspaceChange
}: UnifiedSelectionHeaderProps) {
  const {
    selectedStreamId,
    selectedWorkspaceId,
    setSelectedStreamId,
    setSelectedWorkspaceId,
    userProfile,
    isStreamLoading,
    streams,
    workspaces,
    selectedStreamIcon,
    selectedStudioIcon,
    setSelectedIcon
  } = useStore();
  
  // Determine if we're dealing with a stream or workspace
  const isStreamType = type === 'stream';
  
  // Local state to track the current selection
  const [localSelectedId, setLocalSelectedId] = useState<number | null>(
    isStreamType ? selectedStreamId : selectedWorkspaceId
  );

  // Get the appropriate items based on type
  const items = isStreamType
    ? streams || (userProfile?.streams || []) 
    : workspaces || (userProfile?.workspaces || []);
  
  // Find the selected item
  const selectedItem = items.find(item => getId(item) === (isStreamType ? selectedStreamId : selectedWorkspaceId));

  // Get the current selected icon based on the type
  const selectedIcon = isStreamType ? selectedStreamIcon : selectedStudioIcon;

  // Update local state when store changes
  useEffect(() => {
    if (isStreamType) {
      setLocalSelectedId(selectedStreamId);
    } else {
      setLocalSelectedId(selectedWorkspaceId);
    }
  }, [selectedStreamId, selectedWorkspaceId, isStreamType]);

  // Set initial selection if needed
  useEffect(() => {
    if (items.length > 0 && !localSelectedId) {
      const firstId = getId(items[0]);
      
      console.log(`[UnifiedSelectionHeader] Setting initial ${type} selection to:`, firstId);
      
      if (isStreamType) {
        setSelectedStreamId(firstId);
        onStreamChange?.(firstId);
      } else {
        setSelectedWorkspaceId(firstId);
        onWorkspaceChange?.(firstId);
      }
      
      setLocalSelectedId(firstId);
    }
  }, [items, localSelectedId, setSelectedStreamId, setSelectedWorkspaceId, isStreamType, type, onStreamChange, onWorkspaceChange]);

  // Show loading state if no items
  if (items.length === 0) {
    return (
      <div className="flex items-center justify-between w-full">
        <div className="flex-grow">
          <Select disabled>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={`Loading ${isStreamType ? 'streams' : type === 'studio' ? 'studios' : 'workspaces'}...`} />
            </SelectTrigger>
          </Select>
        </div>
        <div className="flex items-center gap-6 ml-6">
          {/* Disabled icons during loading */}
        </div>
      </div>
    );
  }

  const handleSelectionChange = (value: string) => {
    const id = value ? parseInt(value) : null;
    console.log(`[UnifiedSelectionHeader] ${type} selection changed to:`, id);
    
    // Update local state
    setLocalSelectedId(id);
    
    // Update global state and call change handlers
    if (isStreamType) {
      setSelectedStreamId(id);
      onStreamChange?.(id);
    } else {
      setSelectedWorkspaceId(id);
      onWorkspaceChange?.(id);
    }
  };

  const handleIconSelect = (icon: string) => {
    setSelectedIcon(icon);
  };

  return (
    <div className="flex items-center justify-between w-full">
      {/* Selection Dropdown */}
      <div className="flex-grow">
        <Select
          value={localSelectedId?.toString()}
          onValueChange={handleSelectionChange}
          disabled={isStreamType && isStreamLoading}
        >
          <SelectTrigger className="w-full flex items-center justify-between">
            <SelectValue 
              placeholder={`Select ${isStreamType ? 'stream' : type === 'studio' ? 'studio' : 'workspace'}`}
            >
              {selectedItem && getName(selectedItem)}
            </SelectValue>
            {isStreamType && isStreamLoading ? (
              <Loader2 className="h-4 w-4 ml-2 shrink-0 animate-spin" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
            )}
          </SelectTrigger>
          <SelectContent className="w-[var(--radix-select-trigger-width)] min-w-[var(--radix-select-trigger-width)]">
            {items.map((item) => (
              <SelectItem 
                key={getId(item)} 
                value={getId(item).toString()}
              >
                {getName(item)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Icons based on selected tab */}
      <TooltipProvider>
        <div className="flex items-center gap-6 ml-6">
          {type === 'studio' && (
            <>
              <Tooltip content="Alerts">
                <Button 
                  variant="ghost" 
                  className={cn(
                    "p-2",
                    selectedIcon === 'alerts' && "bg-blue-100 text-blue-600"
                  )} 
                  onClick={() => handleIconSelect('alerts')}
                >
                  <Bell className="h-5 w-5" />
                </Button>
              </Tooltip>
              
              <Tooltip content="Insights">
                <Button 
                  variant="ghost" 
                  className={cn(
                    "p-2",
                    selectedIcon === 'insights' && "bg-blue-100 text-blue-600"
                  )} 
                  onClick={() => handleIconSelect('insights')}
                >
                  <Lightbulb className="h-5 w-5" />
                </Button>
              </Tooltip>
              
              <Tooltip content="Visualization">
                <Button 
                  variant="ghost" 
                  className={cn(
                    "p-2",
                    selectedIcon === 'visualization' && "bg-blue-100 text-blue-600"
                  )} 
                  onClick={() => handleIconSelect('visualization')}
                >
                  <BarChart2 className="h-5 w-5" />
                </Button>
              </Tooltip>
              
              <Tooltip content="Configuration">
                <Button 
                  variant="ghost" 
                  className={cn(
                    "p-2",
                    selectedIcon === 'config' && "bg-blue-100 text-blue-600"
                  )} 
                  onClick={() => handleIconSelect('config')}
                >
                  <Cog className="h-5 w-5" />
                </Button>
              </Tooltip>
            </>
          )}

          {type === 'stream' && (
            <>
              <Tooltip content="News">
                <Button 
                  variant="ghost" 
                  className={cn(
                    "p-2",
                    selectedIcon === 'news' && "bg-blue-100 text-blue-600"
                  )} 
                  onClick={() => handleIconSelect('news')}
                >
                  <Newspaper className="h-5 w-5" />
                </Button>
              </Tooltip>
              
              <Tooltip content="Views">
                <Button 
                  variant="ghost" 
                  className={cn(
                    "p-2",
                    selectedIcon === 'views' && "bg-blue-100 text-blue-600"
                  )} 
                  onClick={() => handleIconSelect('views')}
                >
                  <Eye className="h-5 w-5" />
                </Button>
              </Tooltip>
              
              <Tooltip content="Visualization">
                <Button 
                  variant="ghost" 
                  className={cn(
                    "p-2",
                    selectedIcon === 'visualization' && "bg-blue-100 text-blue-600"
                  )} 
                  onClick={() => handleIconSelect('visualization')}
                >
                  <BarChart2 className="h-5 w-5" />
                </Button>
              </Tooltip>
              
              <Tooltip content="Configuration">
                <Button 
                  variant="ghost" 
                  className={cn(
                    "p-2",
                    selectedIcon === 'config' && "bg-blue-100 text-blue-600"
                  )} 
                  onClick={() => handleIconSelect('config')}
                >
                  <Settings className="h-5 w-5" />
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      </TooltipProvider>
    </div>
  );
} 